import { queryOptions } from "@tanstack/react-query";
import { apiBase, apiUrl } from "@/lib/api-url";
import type { VideoItem, VideoTag } from "@/types";

export interface VideoListFilters {
  tag?: string;
  q?: string;
  status?: "draft" | "published" | "all";
  page?: number;
  limit?: number;
}

export interface VideoListResponse {
  data: VideoItem[];
  total: number;
  page: number;
}

export const videosQueryOptions = (filters: VideoListFilters = {}) =>
  queryOptions({
    queryKey: ["videos", "list", filters],
    queryFn: async (): Promise<VideoListResponse> => {
      const url = new URL("/api/videos", apiBase());
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch videos");
      return res.json();
    },
  });

export const videoQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["videos", "detail", slug],
    queryFn: async (): Promise<VideoItem & { related: VideoItem[] }> => {
      const res = await fetch(apiUrl(`/api/videos/${encodeURIComponent(slug)}?scope=public`));
      if (!res.ok) throw new Error("Video not found");
      return res.json();
    },
  });

export const videoByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["videos", "by-id", id],
    queryFn: async (): Promise<VideoItem> => {
      const res = await fetch(apiUrl(`/api/videos/by-id/${id}`));
      if (!res.ok) throw new Error("Video not found");
      return res.json();
    },
  });

export const videoTagsQueryOptions = queryOptions({
  queryKey: ["videos", "tags", "public"] as const,
  queryFn: async (): Promise<{ data: VideoTag[] }> => {
    const res = await fetch(apiUrl("/api/video-tags?scope=public"));
    if (!res.ok) throw new Error("Failed to fetch video tags");
    return res.json();
  },
});

export const adminVideoTagsQueryOptions = queryOptions({
  queryKey: ["videos", "tags", "admin"] as const,
  queryFn: async (): Promise<{ data: VideoTag[] }> => {
    const res = await fetch(apiUrl("/api/video-tags"));
    if (!res.ok) throw new Error("Failed to fetch video tags");
    return res.json();
  },
});

/**
 * SSR-cache dependency key for every public video list variant. Write handlers
 * invalidate this one key; the per-filter query keys above all start with it.
 */
export const publicVideoListQuery = { queryKey: ["videos", "list"] as const };
